import { computed, signal, WritableSignal } from '@angular/core';
import { AdvancedFilterDto, AdvancedFilterLogicalOperator, BasicFilterDto, DynamicFilterDto, DynamicFilterValue, ExprFilterDto, FilterDto, FilterType, RelativeDateFilterDto, RelativeDateOrTimeOperator, RelativeDateTimeFilterUnit, RelativeTimeFilterDto, SchemaFieldTargetDto, TopNFilterDto } from 'src/app/services/api.services';
import { DataTypeMap } from 'src/app/shared/compatibility/filter.models';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { sequenceEqual } from '../../shared/utils/sequenceEqual';
import {
    AdvancedPredicate as _AdvancedPredicate,
    BasicPredicate as _BasicPredicate,
    DynamicPredicate as _DynamicPredicate,
    ExprPredicate as _ExprPredicate,
    Predicate as _Predicate,
    RelativeDatePredicate as _RelativeDatePredicate,
    RelativeTimePredicate as _RelativeTimePredicate,
    TopNPredicate as _TopNPredicate,
    PredicateType,
} from '../utils/Predicate';
import { VisualFieldTarget } from './VisualFieldTarget';

let nextFilterId = 1;

export class VisualFilter {
  readonly id = nextFilterId++;

  readonly predicate: WritableSignal<VisualFilter.Predicate>;
  readonly disabled: WritableSignal<boolean>;
  readonly collapsed: WritableSignal<boolean>;

  readonly shortTitle = computed(() => this.target()?.shortTitle);
  readonly longTitle = computed(() => this.target()?.longTitle);
  readonly icon = computed(() => this.target()?.icon);
  readonly typeName = computed(() => VisualFilter.Predicate.getTypeName(this.predicate()?.type));

  constructor(properties: VisualFilter.Init) {
    this.predicate = signal(properties.predicate);
    this.disabled = signal(properties.disabled);
    this.collapsed = signal(properties.collapsed);
  }
  
  readonly target = computed(() => this.predicate()?.target);

  readonly isComplete = computed(() => {
    let predicate = this.predicate();
    if (!predicate)
      return false;

    return VisualFilter.Predicate.isComplete(predicate);
  });

  readonly isValid = computed(() => {
    return this.target()?.isValid;
  });

  createFilterDto() {
    return VisualFilter.Predicate.createFilterDto(this.predicate(), this.disabled());
  }
}

export namespace VisualFilter {
  export interface Init {
    predicate: VisualFilter.Predicate;
    disabled?: boolean;
    collapsed?: boolean;
  }

  export type Predicate = BasicPredicate | AdvancedPredicate | DynamicPredicate | RelativeDatePredicate | RelativeTimePredicate | TopNPredicate | ExprPredicate;
  export type BasicPredicate = _BasicPredicate<VisualFieldTarget>;
  export type AdvancedPredicate = _AdvancedPredicate<VisualFieldTarget>;
  export type DynamicPredicate = _DynamicPredicate<VisualFieldTarget>;
  export type RelativeDatePredicate = _RelativeDatePredicate<VisualFieldTarget>;
  export type RelativeTimePredicate = _RelativeTimePredicate<VisualFieldTarget>;
  export type TopNPredicate = _TopNPredicate<VisualFieldTarget>;
  export type ExprPredicate = _ExprPredicate<VisualFieldTarget>;

  export namespace Predicate {
    export namespace Condition {
      export function equal(a: _Predicate.Condition, b: _Predicate.Condition) {
        if (a === b)
          return true;

        if (isNullOrUndefined(a) || isNullOrUndefined(b))
          return isNullOrUndefined(a) && isNullOrUndefined(b);

        if (a.type !== b.type)
          return false;

        return a.value == b.value;
      }
    }

    export function arrayEqual(a: ReadonlyArray<Predicate>, b: ReadonlyArray<Predicate>) {
      if (a === b)
        return true;

      if (!a || !b) 
        return !a && !b;

      if (a?.length != b?.length)
        return false;

      return !a.some((x, i) => !equal(x, b[i]));
    }

    export function equal(a: Predicate, b: Predicate) {
      if (a === b)
        return true;

      if (isNullOrUndefined(a) || isNullOrUndefined(b))
        return isNullOrUndefined(a) && isNullOrUndefined(b);

      if (a.type !== b.type)
        return false;

      if (a.type === PredicateType.Advanced && b.type === PredicateType.Advanced) {
        return a.logicalOperator === b.logicalOperator
            && VisualFieldTarget.equal(a.target, b.target)
            && sequenceEqual(a.conditions, b.conditions, { equal: Predicate.Condition.equal });
      }

      if (a.type === PredicateType.Basic && b.type === PredicateType.Basic) {
        return a.operator === b.operator
            && VisualFieldTarget.equal(a.target, b.target)
            && sequenceEqual(a.values, b.values);
      }

      if (a.type === PredicateType.Dynamic && b.type === PredicateType.Dynamic) {
        return a.kind === b.kind
            && VisualFieldTarget.equal(a.target, b.target);
      }

      if (a.type === PredicateType.Expr && b.type === PredicateType.Expr) {
        return VisualFieldTarget.equal(a.target, b.target)
            && a.expr == b.expr;
      }

      if (a.type === PredicateType.RelativeDate && b.type === PredicateType.RelativeDate) {
        return a.operator === b.operator
            && VisualFieldTarget.equal(a.target, b.target)
            && a.firstUnitCount  == b.firstUnitCount
            && a.firstUnitType   == b.firstUnitType
            && a.secondUnitCount == b.secondUnitCount
            && a.secondUnitType  == b.secondUnitType
            && a.includeToday    == b.includeToday;
      }

      if (a.type === PredicateType.RelativeTime && b.type === PredicateType.RelativeTime) {
        return a.operator === b.operator
            && VisualFieldTarget.equal(a.target, b.target)
            && a.firstUnitCount  == b.firstUnitCount
            && a.firstUnitType   == b.firstUnitType
            && a.secondUnitCount == b.secondUnitCount
            && a.secondUnitType  == b.secondUnitType;
      }

      if (a.type === PredicateType.TopN && b.type === PredicateType.TopN) {
        return a.operator === b.operator
            && VisualFieldTarget.equal(a.target, b.target)
            && a.maxCount == b.maxCount;
      }

      return false;
    }

    export function normalize(predicate: Predicate) {
      return _Predicate.normalize(predicate);
    }

    export function isComplete(predicate: Predicate) {
      return _Predicate.isComplete(predicate);
    }

    export function fromFilterDto(
      dto: FilterDto,
      fieldResolver: (target: SchemaFieldTargetDto) => VisualFieldTarget): Predicate {
      return _Predicate.fromFilterDto<VisualFieldTarget>(dto, fieldResolver);
    }

    export function fromTarget(target: VisualFieldTarget, predicateType?: PredicateType): Predicate {
      if (isNullOrUndefined(predicateType))
        predicateType = target.isAggregated ? PredicateType.Advanced : PredicateType.Basic;

      if (!allowsAggregatedTarget(predicateType) && target.isAggregated) {
        if (!target.canAssignAggregation) {
          throw new Error(`Cannot unassign aggregation from the target, but the predicate only allows unaggregated targets`, {
            cause: { predicateType, target }
          });
        }

        target = target.withAggregation(null);
      }

      switch (predicateType) {
        case PredicateType.Basic:
          return <BasicPredicate>{
            type: PredicateType.Basic,
            target,
            operator: _Predicate.BasicOperator.All,
            values: [],
          };

        case PredicateType.Advanced:
          return <AdvancedPredicate>{
            type: PredicateType.Advanced,
            target,
            logicalOperator: _Predicate.LogicalOperator.And,
            conditions: [
              {
                type: <any>DataTypeMap[target.dataType.value]?.operators?.advancedCondition[0]?.id ?? _Predicate.ConditionType.Is,
                value: null
              }
            ]
          };

        case PredicateType.RelativeDate:
          return <RelativeDatePredicate>{
            type: PredicateType.RelativeDate,
            target,
            firstUnitCount: 1,
            firstUnitType: RelativeDateTimeFilterUnit.Years,
            secondUnitCount: 1,
            secondUnitType: RelativeDateTimeFilterUnit.Years,
            operator: RelativeDateOrTimeOperator.InLast,
            includeToday: true
          };

        case PredicateType.RelativeTime:
          return <RelativeTimePredicate>{
            type: PredicateType.RelativeTime,
            target,
            firstUnitCount: 1,
            firstUnitType: RelativeDateTimeFilterUnit.Hours,
            secondUnitCount: 1,
            secondUnitType: RelativeDateTimeFilterUnit.Hours,
            operator: RelativeDateOrTimeOperator.InLast,
          };

        case PredicateType.TopN:
          return <TopNPredicate>{
            type: PredicateType.TopN,
            target,
            maxCount: 10,
            operator: _Predicate.TopNOperator.Top
          };

        case PredicateType.Dynamic:
          return <DynamicPredicate>{
            type: PredicateType.Dynamic,
            target,
            kind: (target.shortTitle.toLowerCase().indexOf('code') >= 0)
              ? DynamicFilterValue.SpendEmployeeCode
              : DynamicFilterValue.SpendDepartmentNames
          };
      }
    }
    
    export function allowsAggregatedTarget(predicateType: PredicateType) {
      return ![
        PredicateType.Basic,
        PredicateType.Dynamic, 
        PredicateType.RelativeDate, 
        PredicateType.RelativeTime
      ].includes(predicateType);
    }

    export function createFilterDto(predicate: Predicate, disabled?: boolean) {
      let enabled = !disabled;

      if (predicate.type === PredicateType.Advanced) {
        return <AdvancedFilterDto>{
          filterType: FilterType.Advanced,
          enabled,
          target: predicate.target?.schemaFieldTargetDto,
          logicalOperator: <any>predicate.logicalOperator,
          conditions: predicate.conditions.map(x => ({
            operator: <any>x.type,
            value: x.value
          }))
        };
      }
      else if (predicate.type === PredicateType.Basic) {
        return <BasicFilterDto>{
          filterType: FilterType.Basic,
          enabled,
          target: predicate.target?.schemaFieldTargetDto,
          operator: <any>predicate.operator,
          values: predicate.values
        };
      }
      else if (predicate.type === PredicateType.Dynamic) {
        return <DynamicFilterDto>{
          filterType: FilterType.Dynamic,
          enabled,
          target: predicate.target?.schemaFieldTargetDto,
          value: <any>predicate.kind
        };
      }
      else if (predicate.type === PredicateType.RelativeDate) {
        return <RelativeDateFilterDto>{
          filterType: FilterType.RelativeDate,
          enabled,
          target: predicate.target?.schemaFieldTargetDto,
          operator: <any>predicate.operator,
          timeUnitsCount: predicate.firstUnitCount,
          timeUnitType: <any>predicate.firstUnitType,
          timeUnitsCount2: predicate.secondUnitCount,
          timeUnitType2: <any>predicate.secondUnitType,
          includeToday: predicate.includeToday
        };
      }
      else if (predicate.type === PredicateType.RelativeTime) {
        return <RelativeTimeFilterDto>{
          filterType: FilterType.RelativeTime,
          enabled,
          target: predicate.target?.schemaFieldTargetDto,
          operator: <any>predicate.operator,
          timeUnitsCount: predicate.firstUnitCount,
          timeUnitsType: <any>predicate.firstUnitType,
          timeUnitsCount2: predicate.secondUnitCount,
          timeUnitsType2: <any>predicate.secondUnitType,
        };
      }
      else if (predicate.type === PredicateType.TopN) {
        return <TopNFilterDto>{
          filterType: FilterType.TopN,
          enabled,
          orderBy: predicate.target?.schemaFieldTargetDto,
          operator: <any>predicate.operator,
          itemCount: predicate.maxCount
        };
      }
      else if (predicate.type === PredicateType.Expr) {
        return <ExprFilterDto>{
          filterType: FilterType.Expr,
          enabled,
          schema: predicate.target?.schemaFieldTargetDto?.schema,
          expr: predicate.expr
        };
      }
    }

    export function formatTitle(predicate: Predicate) {
      let target = predicate.target;
      let targetText = target.aggregation ? target.aggregation.formatAggregationOfText(target.shortTitle) : target.shortTitle;
      // TODO: predicate format title
    }

    export function getTypeName(type: PredicateType) {
      switch (type) {
        case PredicateType.Advanced: return 'Advanced';
        case PredicateType.Basic: return 'Basic';
        case PredicateType.Dynamic: return 'Dynamic';
        case PredicateType.Expr: return 'Expr';
        case PredicateType.RelativeDate: return 'Relative Date';
        case PredicateType.RelativeTime: return 'Relative Time';
        case PredicateType.TopN: return 'Top N';
      }

      return '';
    }
  }


  export interface FilterGroup {
    readonly operator: AdvancedFilterLogicalOperator;
    readonly filters: ReadonlyArray<VisualFilter>;
  };

  export interface PredicateGroup {
    readonly operator: AdvancedFilterLogicalOperator;
    readonly filters: ReadonlyArray<Predicate>;
  };

  export namespace PredicateGroup {
    export function arrayEqual(a: ReadonlyArray<PredicateGroup>, b: ReadonlyArray<PredicateGroup>) {
      if (a === b)
        return true;

      if (!a || !b) 
        return !a && !b;
        
      if (a.filter.length != b.filter.length)
        return false;

      return !a.some((x, i) => !equal(x, b[i]));
    }

    export function equal(a: PredicateGroup, b: PredicateGroup) {
      if (a === b)
        return true;
        
      if (!a || !b) 
        return !a && !b;

      if (a.operator != b.operator)
        return false;

      return Predicate.arrayEqual(a.filters, b.filters);
    }
  }


  export function arrayEqual(a: ReadonlyArray<VisualFilter>, b: ReadonlyArray<VisualFilter>) {
    if (a === b)
      return true;

    if (!a || !b) 
      return !a && !b;

    if (a?.length != b?.length)
      return false;

    return !a.some((x, i) => !equal(x, b[i]));
  }

  export function equal(a: VisualFilter, b: VisualFilter) {
    if (a === b)
      return true;

    if (isNullOrUndefined(a) || isNullOrUndefined(b))
      return isNullOrUndefined(a) && isNullOrUndefined(b);

    if (!a.target().equals(b.target()))
      return false;

    let predicateA = a.predicate();
    let predicateB = b.predicate();

    return Predicate.equal(predicateA, predicateB);
  }
}
